// Apex Athletic Mind — Coach Mindset Quiz data
// 4 coach archetypes, 8 sport-agnostic questions, 4 unique framework
// recommendations. Mirrors the shape of the athlete + parent quizData exports.

export const ARCHETYPES = {
  drill_sergeant: {
    key: 'drill_sergeant',
    name: 'The Drill Sergeant',
    headline: 'You build standards. You bring intensity. They\u2019re scared to disappoint you.',
    color: '#004C54',
    description:
      "You teach kids how to compete. They get tougher in your program. But the by-product " +
      "is that they start hiding mistakes from you instead of bringing them. They tighten up " +
      "under pressure because the cost of failing in front of you feels too high. You don't " +
      "need to lower the standard — you need a connection window so they trust you enough to " +
      "fail in front of you and grow because of it.",
    framework_name: 'The Connection Window',
    framework_tag: 'FIRST 90 SECONDS OF PRACTICE',
    framework_pitch:
      "A repeatable 90-second start-of-practice ritual that builds trust without losing " +
      "intensity. So when the standard hits at minute 4, your athletes meet it instead of " +
      "hiding from it.",
    growth_line:
      "They'll run through a wall for you, but only if they believe you'll catch them when they fall short of one.",
  },
  mentor: {
    key: 'mentor',
    name: 'The Mentor',
    headline: 'They love playing for you. But the execution gap keeps showing up.',
    color: '#004C54',
    description:
      "Your athletes love you. Your culture is real. The buy-in is there. But the gap shows " +
      "up in games — beliefs don't quite turn into reps, and standards live in conversation " +
      "more than in the film room. You don't need a culture change — you need a bridge that " +
      "connects what they believe to what they do, under pressure.",
    framework_name: 'The Standards Bridge',
    framework_tag: 'IN-PRACTICE',
    framework_pitch:
      "A weekly tool that translates your culture words (\"compete,\" \"trust,\" \"next play\") " +
      "into 1\u20132 observable behaviors per athlete you can actually call out in real time. " +
      "Turns the belief you've built into reps that show up on Saturday.",
    growth_line:
      "Belief without behavior is a poster. Belief tied to behavior is a program.",
  },
  tactician: {
    key: 'tactician',
    name: 'The Tactician',
    headline: 'You see the game like a chess board. They feel like pieces.',
    color: '#004C54',
    description:
      "You out-prepare every opponent. Your gameplan is sharper than anyone in your league. " +
      "But somewhere in the brilliance, your athletes started executing your plan instead of " +
      "OWNING it — and when it cracks under pressure, they look at you to fix it instead of " +
      "at each other. You don't need to dial back the prep — you need to teach the WHY before " +
      "the HOW so they own the call when it gets ugly.",
    framework_name: 'The Why Stack',
    framework_tag: 'PRE-PRACTICE BRIEFING',
    framework_pitch:
      "A 3-layer briefing format (What \u2192 Why \u2192 What it lets us do) that turns every drill " +
      "and call into something your athletes own instead of execute. So when the plan breaks " +
      "mid-game, they adapt to the WHY instead of waiting for you.",
    growth_line:
      "A team that executes your plan will win games. A team that understands your plan will win seasons.",
  },
  players_coach: {
    key: 'players_coach',
    name: "The Player's Coach",
    headline: "They'd play through anything for you. But you'd rather not have the hard conversation.",
    color: '#004C54',
    description:
      "You see your athletes as people, not pieces. The trust is REAL, and they'd run through " +
      "a wall for you. But the cost is that hard conversations get softened, accountability " +
      "gets traded for relationship, and the slippage shows up in habits that don't get " +
      "corrected. You don't need to become colder — you need a way to deliver hard truth " +
      "WITHOUT spending the trust you've built.",
    framework_name: 'The Honest Mirror',
    framework_tag: '1-ON-1 FEEDBACK',
    framework_pitch:
      "A 3-step feedback protocol (Witness \u2192 Standard \u2192 Belief) that gives you a way to " +
      "deliver hard truth that lands without burning down the relationship that makes you " +
      "effective. For coaches whose superpower is love, and whose blind spot is candor.",
    growth_line:
      "The kids who love you the most are the ones who can take the most honest version of you. Don't water it down.",
  },
};

export const QUESTIONS = [
  {
    id: 'q1',
    scene: "A starter just made a costly turnover in a tight game. They come back to the bench head-down.",
    stem: "What's the first thing out of your mouth?",
    options: [
      { archetype: 'drill_sergeant', text: "\"We don't carry mistakes. Sit down, reset, you're going back in.\"" },
      { archetype: 'mentor',         text: "Sit next to them. \"Tell me what you saw on the play.\"" },
      { archetype: 'tactician',      text: "Whiteboard out. Show them the read they missed and what to do next time." },
      { archetype: 'players_coach',  text: "\"Hey. We're good. Breathe. Get me the next one.\"" },
    ],
  },
  {
    id: 'q2',
    scene: "Halftime, down by 12. Locker room is dead.",
    stem: "What's the move?",
    options: [
      { archetype: 'drill_sergeant', text: "Light a fire. They need to feel uncomfortable to wake up." },
      { archetype: 'mentor',         text: "Anchor them \u2014 remind them who they are and what they came to do." },
      { archetype: 'tactician',      text: "Adjustments. New coverage. New offensive set. Execution." },
      { archetype: 'players_coach',  text: "Connect first. Get one player to speak up. Pull the team back together." },
    ],
  },
  {
    id: 'q3',
    scene: "A senior leader keeps showing up late to practice.",
    stem: "How do you handle it?",
    options: [
      { archetype: 'drill_sergeant', text: "Consequences. Running. Losing their spot if it continues." },
      { archetype: 'players_coach',  text: "Pull them aside. Honest conversation. \"What's going on?\"" },
      { archetype: 'mentor',         text: "Team meeting. Standards are everyone's responsibility, not just one player's." },
      { archetype: 'tactician',      text: "Restructure roles so the team doesn't depend on them showing up." },
    ],
  },
  {
    id: 'q4',
    scene: "A talented athlete is over-thinking and getting tight in games.",
    stem: "Your read on what they need?",
    options: [
      { archetype: 'drill_sergeant', text: "More reps. Tougher practice. Tougher than the game." },
      { archetype: 'tactician',      text: "Simpler reads. Cut the playbook in half. Free them up." },
      { archetype: 'players_coach',  text: "A conversation. They need to know you've got their back." },
      { archetype: 'mentor',         text: "Identity work. Remind them who they are when nothing's at stake." },
    ],
  },
  {
    id: 'q5',
    scene: "End of a practice that was sluggish. Energy was off all day.",
    stem: "How do you wrap it up?",
    options: [
      { archetype: 'drill_sergeant', text: "Conditioning. Tomorrow will be different." },
      { archetype: 'mentor',         text: "Circle up. Talk about the gap between who they say they are and what just happened." },
      { archetype: 'tactician',      text: "Cut it short. Reset tomorrow with clearer focus and a better plan." },
      { archetype: 'players_coach',  text: "Read the room. Maybe they need rest more than punishment." },
    ],
  },
  {
    id: 'q6',
    scene: "An athlete with massive talent but inconsistent effort.",
    stem: "How do you coach them?",
    options: [
      { archetype: 'drill_sergeant', text: "Standards. Either they meet them or they don't play." },
      { archetype: 'mentor',         text: "Vision. Help them see who they could become if they bring it every day." },
      { archetype: 'players_coach',  text: "Relationship. They need to know you see them as a person first." },
      { archetype: 'tactician',      text: "Put them in roles where their effort is structurally required to succeed." },
    ],
  },
  {
    id: 'q7',
    scene: "The team just lost a heartbreaker. Last play. Walking off the field.",
    stem: "What's your message?",
    options: [
      { archetype: 'drill_sergeant', text: "\"We're going to remember how this feels. Use it.\"" },
      { archetype: 'players_coach',  text: "\"I'm proud of you. Heads up. We go back to work tomorrow.\"" },
      { archetype: 'mentor',         text: "\"Look at me. This game doesn't define you. The next one starts now.\"" },
      { archetype: 'tactician',      text: "Already breaking down what cost us the game in your head." },
    ],
  },
  {
    id: 'q8',
    scene: "Pre-game speech. Biggest game of the year.",
    stem: "What's the energy?",
    options: [
      { archetype: 'drill_sergeant', text: "Fire. Standards. \"This is who we are. They can't match us.\"" },
      { archetype: 'tactician',      text: "Calm. Detailed. \"Here's what they do. Here's what we do.\"" },
      { archetype: 'mentor',         text: "Identity. \"You're not playing for the scoreboard. You're playing for what you've become.\"" },
      { archetype: 'players_coach',  text: "Connection. \"I love coaching you. Now let's go play free.\"" },
    ],
  },
];

export const REQUIRES_SPORT = false;

export function scoreQuiz(selectedArchetypes) {
  const scores = { drill_sergeant: 0, mentor: 0, tactician: 0, players_coach: 0 };
  selectedArchetypes.forEach((k) => { if (scores[k] != null) scores[k]++; });
  let winner = null;
  let max = -1;
  Object.entries(scores).forEach(([k, v]) => {
    if (v > max) { max = v; winner = k; }
  });
  const tied = Object.entries(scores).filter(([, v]) => v === max).map(([k]) => k);
  if (tied.length > 1) {
    for (let i = selectedArchetypes.length - 1; i >= 0; i--) {
      if (tied.includes(selectedArchetypes[i])) { winner = selectedArchetypes[i]; break; }
    }
  }
  return { winner, scores };
}
